import React, { useState } from "react";
import { FileText, Download, FolderDown, Loader2 } from "lucide-react";

// Gli allegati del corso sono solo PDF (validato lato backend), quindi mostriamo
// un'icona coerente e l'etichetta "PDF" senza dover mappare estensioni.
function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Pannello dei materiali didattici del modulo che lo studente sta seguendo.
 * Usato sia nella colonna destra fissa (schermi larghi) sia inline sotto il
 * video (schermi stretti): è autonomo, con bordo e sfondo propri.
 */
export default function ModuleMaterials({
  attachments = [],
  moduleTitle,
  moduleIndex,
  onDownload,
  className = "",
}) {
  const [busyAll, setBusyAll] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const count = attachments.length;

  const handleOne = async (att) => {
    setBusyId(att.id);
    try {
      await onDownload(att);
    } finally {
      setBusyId(null);
    }
  };

  const handleAll = async () => {
    setBusyAll(true);
    try {
      for (const att of attachments) {
        // Sequenziale: evita che il browser blocchi download multipli simultanei.
        await onDownload(att);
      }
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <div
      data-testid="module-materials"
      className={"bg-white border border-brand-line rounded-sm overflow-hidden " + className}
    >
      {/* Intestazione */}
      <div className="px-4 py-3.5 border-b border-brand-line bg-brand-bg/60">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderDown size={16} className="text-brand-blue shrink-0" />
            <span className="text-sm font-semibold text-brand-ink truncate">
              Materiali del modulo
            </span>
          </div>
          {count > 0 && (
            <span className="shrink-0 text-[11px] font-semibold text-brand-blue bg-brand-blue/10 rounded-sm px-1.5 py-0.5">
              {count} file
            </span>
          )}
        </div>
        {moduleTitle && (
          <div className="text-[11px] uppercase tracking-widest text-brand-gold mt-1.5 truncate">
            {moduleIndex != null ? `Modulo ${moduleIndex + 1} · ` : ""}
            {moduleTitle}
          </div>
        )}
      </div>

      {count === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-muted mb-2">
            <FileText size={18} />
          </div>
          <p className="text-xs text-brand-muted">
            Nessun materiale per questo modulo.
          </p>
        </div>
      ) : (
        <>
          <div className="p-3 space-y-2">
            {attachments.map((att) => (
              <button
                key={att.id}
                onClick={() => handleOne(att)}
                disabled={busyId === att.id || busyAll}
                data-testid={`download-attachment-${att.id}`}
                title={`Scarica ${att.filename}`}
                className="group w-full flex items-center gap-3 rounded-sm border border-brand-line hover:border-brand-blue/40 hover:bg-brand-blue/5 p-2.5 text-left transition-colors disabled:opacity-60"
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-brand-blue/10 text-brand-blue">
                  <FileText size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-brand-ink truncate group-hover:text-brand-blue">
                    {att.filename}
                  </span>
                  <span className="block text-[11px] text-brand-muted">
                    PDF · {formatSize(att.size)}
                  </span>
                </span>
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-brand-muted group-hover:text-brand-blue">
                  {busyId === att.id ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                </span>
              </button>
            ))}
          </div>

          {count > 1 && (
            <div className="px-3 pb-3">
              <button
                onClick={handleAll}
                disabled={busyAll}
                data-testid="download-all-attachments"
                className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {busyAll ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Scarica tutti
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
